// synced from loomweb (block-view iconography) — re-drawn as inline SVG.
/**
 * Inline SVG icons for the ported loom block-views. loomweb uses `lucide-react`;
 * this host has no icon dependency, so each icon the views reference is provided
 * here as a tiny inline SVG with the same visual intent (currentColor stroke, so
 * the `.replay-*` CSS color classes drive the tint). Faithful set: only the
 * glyphs the ported views actually use. Keep in sync when re-syncing the views.
 */
import type { JSX } from "react";

/** Shared props: a class for sizing/color and an aria-hidden default. */
interface IconProps {
  className?: string | undefined;
}

/** Stroke-only 24x24 base; sizing/color come from the className. */
function Svg({ className, children }: IconProps & { children: JSX.Element }): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function TerminalIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <>
        <polyline points="4 17 10 11 4 5" />
        <line x1="12" y1="19" x2="20" y2="19" />
      </>
    </Svg>
  );
}

export function GitBranchIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <>
        <line x1="6" y1="3" x2="6" y2="15" />
        <circle cx="18" cy="6" r="3" />
        <circle cx="6" cy="18" r="3" />
        <path d="M18 9a9 9 0 0 1-9 9" />
      </>
    </Svg>
  );
}

export function FileEditIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <path d="M10.5 13.5 13 16l-3.5 1 0.5-3.5z" />
      </>
    </Svg>
  );
}

export function FileTextIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="8" y1="13" x2="16" y2="13" />
        <line x1="8" y1="17" x2="16" y2="17" />
      </>
    </Svg>
  );
}

export function FolderSearchIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <>
        <path d="M4 4h5l2 2h7a2 2 0 0 1 2 2v3" />
        <path d="M4 4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6" />
        <circle cx="17" cy="17" r="3" />
        <line x1="21" y1="21" x2="19.1" y2="19.1" />
      </>
    </Svg>
  );
}

export function ListTodoIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <>
        <rect x="3" y="5" width="6" height="6" rx="1" />
        <path d="m3 17 2 2 4-4" />
        <line x1="13" y1="6" x2="21" y2="6" />
        <line x1="13" y1="12" x2="21" y2="12" />
        <line x1="13" y1="18" x2="21" y2="18" />
      </>
    </Svg>
  );
}

export function SparklesIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <path d="M12 3l1.9 4.8L18 9.6l-4.1 1.8L12 16l-1.9-4.6L6 9.6l4.1-1.8z" />
    </Svg>
  );
}

export function SearchIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <>
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </>
    </Svg>
  );
}

export function BookOpenIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <>
        <path d="M2 4h6a3 3 0 0 1 3 3v13a2.5 2.5 0 0 0-2.5-2.5H2z" />
        <path d="M22 4h-6a3 3 0 0 0-3 3v13a2.5 2.5 0 0 1 2.5-2.5H22z" />
      </>
    </Svg>
  );
}

export function CheckIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <polyline points="20 6 9 17 4 12" />
    </Svg>
  );
}

export function CircleIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <circle cx="12" cy="12" r="9" />
    </Svg>
  );
}

export function LoaderIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <>
        <line x1="12" y1="2" x2="12" y2="6" />
        <line x1="12" y1="18" x2="12" y2="22" />
        <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
        <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
        <line x1="2" y1="12" x2="6" y2="12" />
        <line x1="18" y1="12" x2="22" y2="12" />
        <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
        <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
      </>
    </Svg>
  );
}

export function AlertTriangleIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <>
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </>
    </Svg>
  );
}

export function EyeIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <>
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
        <circle cx="12" cy="12" r="3" />
      </>
    </Svg>
  );
}

export function ChevronDownIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <polyline points="6 9 12 15 18 9" />
    </Svg>
  );
}

export function ChevronRightIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <polyline points="9 18 15 12 9 6" />
    </Svg>
  );
}

export function MonitorIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <>
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </>
    </Svg>
  );
}

export function XIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </>
    </Svg>
  );
}

export function CloudUploadIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <>
        <path d="M4 14.9A6 6 0 1 1 15.7 8h1.3a4.5 4.5 0 0 1 2.5 8.2" />
        <polyline points="16 16 12 12 8 16" />
        <line x1="12" y1="12" x2="12" y2="21" />
      </>
    </Svg>
  );
}

export function ChevronUpIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <polyline points="18 15 12 9 6 15" />
    </Svg>
  );
}

export function CopyIcon({ className }: IconProps): JSX.Element {
  return (
    <Svg className={className}>
      <>
        <rect x="9" y="9" width="13" height="13" rx="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </>
    </Svg>
  );
}
